import 'twin.macro'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { useTranslation } from '@i18n'
import {
  SearchArticles,
  SearchArticles_searchArticles_articles,
} from '@apollo-types/SearchArticles'
import React, { useCallback, useState, useEffect } from 'react'
import { TrendingKeywords } from '@apollo-types/TrendingKeywords'
import SearchBarContainer from './search-bar'
import ArticleCard from './article-card'
import SearchStyle from './style'
import getSearchResult from './_core/getSearchResult'
import getTrendingKeywords from './_core/getTrendingKeywords'
import TrendingKeywordsContainer from './trending-keywords'
import ErrorNoInternet from '@components/ErrorNoInternet'
import Pagination from './pagination'
import ShowADScript from '@components/ShowADScript'
import { dispatchCustomEvent } from '@libs/customEvents'
import { SearchEvents } from '@libs/customEvents/search'
import { withSearchEventTracking } from './hocs/withSearchEventTracking'
import { searchUrl } from '@libs/appUrl'
import SearchHead from './SearchHead'
import GoogleAnalyticsScriptAmp from './GoogleAnalyticsScript.amp'
import debounce from 'lodash/debounce'
import ArticleCardLoader from '@features/search/article-card/ArticleCardLoader'
import { ChevronDownIcon } from '@components/Icons'
import {
  trackSearchFilterSwitchEvent,
  trackSearchSortSwitchEvent,
} from '@libs/eventTracking/search'
import { ArticleContentType, ArticleSearchType } from '@apollo-types/globalTypes'

interface DropDownProps {
  keyList: any[]
  activeKey: string
  keyword: string
  filter: string
  sort: string
}

const DropDown = (props: DropDownProps) => {
  const { keyList, activeKey, keyword, filter, sort } = props
  const { t } = useTranslation('search')
  const router = useRouter()

  const [isShow, setIsShow] = useState(false)
  const title = t(keyList.find((item) => item === activeKey))

  const onClickRedirect = (item) => {
    setIsShow(false)
    let toPageUrl = ''
    if (activeKey === filter) {
      toPageUrl = searchUrl(keyword, 1, item, sort)
      trackSearchFilterSwitchEvent(window.location.href, toPageUrl, keyword, filter, sort, item)
    } else {
      toPageUrl = searchUrl(keyword, 1, filter, item)
      trackSearchSortSwitchEvent(window.location.href, toPageUrl, keyword, filter, sort, item)
    }
    router.push(toPageUrl)
  }

  useEffect(() => {
    if (!isShow) return
    const onClick = () => {
      setIsShow(false)
      window.removeEventListener('click', onClick)
    }
    window.addEventListener('click', onClick)
    return () => window.removeEventListener('click', onClick)
  }, [isShow])

  return (
    <>
      <SearchStyle.DropdownAction
        style={{ color: isShow ? '#333333' : '#999999' }}
        onClick={() => setIsShow(true)}
      >
        <span>{title}</span>
        <ChevronDownIcon fill="#333333" height="16" width="16" />
        <SearchStyle.PopBox style={{ display: isShow ? 'block' : 'none' }}>
          {keyList.map((item: string, index) => {
            return (
              <SearchStyle.PopItem
                key={`${item}_${index}`}
                className={activeKey === item ? 'active' : ''}
                onClick={() => onClickRedirect(item)}
              >
                {t(item)}
              </SearchStyle.PopItem>
            )
          })}
        </SearchStyle.PopBox>
      </SearchStyle.DropdownAction>
    </>
  )
}

interface SearchResultProps {
  keyword: string
  currentPage: number // 1~XXX
  totalCount: number
  limit: number
  articles: SearchArticles_searchArticles_articles[]
  pageUrl: (page: number) => string
  isLoading: boolean
  filter: ArticleContentType
  sort: ArticleSearchType
}

const SearchResult = (props: SearchResultProps) => {
  const { t } = useTranslation('search')
  const { articles, currentPage, totalCount, pageUrl, keyword, limit, isLoading, filter, sort } =
    props

  const keywordLimit10 = keyword.length > 10 ? keyword.slice(0, 10) + '...' : keyword

  const sortKeyList = [ArticleSearchType.RELEVANT, ArticleSearchType.LATEST, ArticleSearchType.HOT]
  const filterKeyList = [ArticleContentType.ALL, ArticleContentType.PUGC, ArticleContentType.PGC]

  const shouldNoFollow = useCallback(
    (page: number) => {
      if (sort !== ArticleSearchType.LATEST) return false

      return page > 1
    },
    [sort],
  )

  return (
    <>
      <SearchStyle.H1>
        <span className="keyword">{keywordLimit10}</span>
        <span>{t(['resultTitle', '搜尋結果'])}</span>
      </SearchStyle.H1>
      <SearchStyle.DropdownBar>
        <DropDown
          keyList={sortKeyList}
          activeKey={sort}
          keyword={keyword}
          filter={filter}
          sort={sort}
        />

        <DropDown
          keyList={filterKeyList}
          activeKey={filter}
          keyword={keyword}
          filter={filter}
          sort={sort}
        />
      </SearchStyle.DropdownBar>
      {articles.length > 0 ? (
        <>
          <SearchStyle.ResultContainer>
            {!isLoading &&
              articles.map((article, index) => {
                const cardIndex = (currentPage - 1) * limit + index
                return (
                  <SearchStyle.CardWrapper key={article.id}>
                    <ArticleCard
                      index={cardIndex}
                      key={article.id}
                      article={article}
                      keyword={keyword}
                      filter={filter}
                      sort={sort}
                    />
                  </SearchStyle.CardWrapper>
                )
              })}
          </SearchStyle.ResultContainer>
          <SearchStyle.Footer>
            {!isLoading && (
              <Pagination
                currentPage={currentPage}
                totalCount={totalCount}
                pageUrl={pageUrl}
                shouldNoFollow={shouldNoFollow}
              />
            )}
          </SearchStyle.Footer>
        </>
      ) : (
        <SearchStyle.EmptyResultContainer>
          <ErrorNoInternet />
          <p>{t(['notFound', '找不到您要的搜尋結果'])}</p>
        </SearchStyle.EmptyResultContainer>
      )}
    </>
  )
}

interface SearchOptionsSelectedFieldProps {
  trendingKeywords?: string[]
  keywordOnClick?: (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void
}

const SearchOptionsSelectedField = (props: SearchOptionsSelectedFieldProps) => {
  // const { t } = useTranslation('search')
  const { trendingKeywords, keywordOnClick } = props
  return (
    <>
      <SearchStyle.Section>
        <TrendingKeywordsContainer
          trendingKeywords={trendingKeywords}
          keywordOnClick={keywordOnClick}
        />
      </SearchStyle.Section>
      {/* <SearchStyle.Section>
        <div className="title">
          <ClockIcon />
          <SearchStyle.Title>{t(['historySearch', '搜尋紀錄'])}</SearchStyle.Title>
          <SearchStyle.ClearBtn>{t(['clear', '清空'])}</SearchStyle.ClearBtn>
        </div>
        <div className="content">
          {['台北必吃', '推薦美食'].map((value) => (
            <Chip key={value} variant={ChipVariant.default}>
              {value}
            </Chip>
          ))}
        </div>
      </SearchStyle.Section> */}
    </>
  )
}

interface SearchProps {
  searchResult: {
    data: SearchArticles
  }
  keyword: string
  currentPage: number
  limit: number
  trendingKeywordsResponse: {
    data: TrendingKeywords
  }
  referrer?: string
  fromPage?: string
  filter?: ArticleContentType
  sort?: ArticleSearchType
}

const Search = (props: SearchProps) => {
  const { searchResult, keyword, currentPage, trendingKeywordsResponse, limit, filter, sort } =
    props
  const router = useRouter()
  const defaultPublishDate = '2021-02-24T00:00:00.000Z' // 搜尋頁上線時間
  const articles = searchResult?.data?.searchArticles?.articles ?? []
  const totalCount = searchResult?.data?.searchArticles?.totalCount ?? 10
  const publishedAt = searchResult?.data?.searchArticles?.publishedAt ?? defaultPublishDate
  const modifiedAt = searchResult?.data?.searchArticles?.modifiedAt ?? defaultPublishDate
  const trendingKeywords = trendingKeywordsResponse?.data?.trendingKeywords ?? []

  const [isLoading, setIsLoading] = useState(false)

  const handleUserInputSearch = useCallback(
    debounce((keyword: string) => {
      // tracking web-search.search-bar.search
      dispatchCustomEvent(SearchEvents.SEARCH_BAR_SEARCH, { searchKeyword: keyword })
      router.push(searchUrl(keyword, 1, ArticleContentType.ALL, ArticleSearchType.RELEVANT))
    }, 500),
    [],
  )

  const handleClickKeywordSearch = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    const value = event.currentTarget.innerHTML
    // filter emoji
    const filteredValue = value
      .replace(/([\uE000-\uF8FF]|\uD83C[\uDF00-\uDFFF]|\uD83D[\uDC00-\uDDFF]|\u2764|\ufe0f)/g, '')
      .trim()
    dispatchCustomEvent(SearchEvents.SEARCH_HOT_KEYWORD_SEARCH, {
      searchKeyword: filteredValue,
    })
    router.push(searchUrl(filteredValue, 1, filter, sort))
  }
  const pageUrl = (page: number) => searchUrl(keyword, page, filter, sort)

  useEffect(() => {
    const handleStart = () => setIsLoading(true)
    const handleStop = () => setIsLoading(false)
    router.events.on('routeChangeStart', handleStart)
    router.events.on('routeChangeComplete', handleStop)
    return () => {
      router.events.off('routeChangeStart', handleStart)
      router.events.off('routeChangeComplete', handleStop)
    }
  }, [router])

  const loaderArray = [...new Array(10)]

  return (
    <>
      <SearchHead
        publishedAt={publishedAt}
        modifiedAt={modifiedAt}
        keyword={keyword}
        currentPage={currentPage}
        articles={articles}
        trendingKeywords={trendingKeywords}
        sort={sort}
      />
      <ShowADScript />
      <GoogleAnalyticsScriptAmp />
      <SearchStyle.Layout>
        <SearchStyle.Container>
          <SearchStyle.InputArea>
            <SearchBarContainer onSearch={handleUserInputSearch} value={keyword} />
          </SearchStyle.InputArea>
          {keyword ? (
            <SearchResult
              keyword={keyword}
              articles={articles}
              currentPage={currentPage}
              totalCount={totalCount}
              limit={limit}
              pageUrl={pageUrl}
              filter={filter}
              sort={sort}
              isLoading={isLoading}
            />
          ) : (
            <SearchOptionsSelectedField
              trendingKeywords={trendingKeywords}
              keywordOnClick={handleClickKeywordSearch}
            />
          )}
          <SearchStyle.LoaderBox>
            {isLoading && loaderArray.map((_, index) => <ArticleCardLoader key={index} />)}
          </SearchStyle.LoaderBox>
        </SearchStyle.Container>
      </SearchStyle.Layout>
    </>
  )
}

export default withSearchEventTracking(Search)

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const query = ctx.query as Record<string, string>
  const keyword = query.keyword ?? ''
  const currentPage = parseInt(query.page, 10) || 1
  const limit = 10
  const filter = (query.filter as ArticleContentType) ?? ArticleContentType.ALL
  const sort = (query.sort as ArticleSearchType) ?? ArticleSearchType.RELEVANT

  // 如果網址是 /search?keyword=:keyword
  // 轉址到 /search/:keyword
  if (keyword && !ctx.params?.keyword) {
    return {
      props: {},
      redirect: { destination: searchUrl(keyword, currentPage, filter, sort), permanent: true },
    }
  }

  // 當網址是 /search/:keyword
  // 但是沒有 filter 以及 sort 時
  // 轉址到 /search/:keyword?filter=ALL&sort=RELEVANT
  if (ctx.params?.keyword && (!query.filter || !query.sort)) {
    return {
      props: {},
      redirect: { destination: searchUrl(keyword, currentPage, filter, sort), permanent: true },
    }
  }

  const searchResult = keyword
    ? await getSearchResult({
        ctx,
        keyword,
        currentPage,
        limit,
        contentType: filter,
        searchType: sort,
      })
    : {}
  const trendingKeywordsResponse = await getTrendingKeywords({ ctx })
  const referrer = ctx?.req?.headers?.referer
  const [, searchParams = ''] = ctx.req.url.split('?')
  const fromPage = require('qs').parse(searchParams, { allowDots: true }).fromPage ?? {}
  if (referrer && !fromPage.pageUrl) {
    fromPage.pageUrl = referrer
  }

  return {
    props: {
      searchResult,
      keyword,
      currentPage,
      trendingKeywordsResponse,
      fromPage,
      limit,
      filter,
      sort,
    },
  }
}
